import {useState, useEffect} from "react"

import {VIEW_MODES, ViewMode} from "../archiveTypes"

interface UseViewModeManagerProps {
  hasSearchTerm: boolean
  hasDateFilters: boolean
  hasParticipantFilters: boolean
}

interface UseViewModeManagerReturn {
  view: ViewMode
  isSearchMode: boolean
  resetToMonthView: () => void
  setViewMode: (mode: ViewMode) => void
}

/**
 * Hook for managing view mode switching between month view and search results view
 *
 * Automatically switches to search mode when any search activity is detected,
 * and provides manual control for resetting to month view.
 *
 * @param hasSearchTerm - Whether there's an active search term
 * @param hasDateFilters - Whether date range filters are applied
 * @param hasParticipantFilters - Whether participant filters are applied
 */
export const useViewModeManager = ({
  hasSearchTerm,
  hasDateFilters,
  hasParticipantFilters,
}: UseViewModeManagerProps): UseViewModeManagerReturn => {
  const [view, setView] = useState<ViewMode>(VIEW_MODES.MONTH_VIEW)

  // Determine if any search activity is happening
  const hasActiveSearch =
    hasSearchTerm || hasDateFilters || hasParticipantFilters

  // Automatically switch view mode based on search activity
  useEffect(() => {
    const newView = hasActiveSearch
      ? VIEW_MODES.SEARCH_RESULTS
      : VIEW_MODES.MONTH_VIEW
    setView(newView)
  }, [hasActiveSearch])

  // Manual reset to month view (useful for clearing operations)
  const resetToMonthView = () => {
    setView(VIEW_MODES.MONTH_VIEW)
  }

  // Manual view mode setter (for future extensibility)
  const setViewMode = (mode: ViewMode) => {
    setView(mode)
  }

  return {
    view,
    isSearchMode: hasActiveSearch,
    resetToMonthView,
    setViewMode,
  }
}
